import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Circle, CheckCircle, XCircle } from "lucide-react";

const ROW_COUNT = 5;
const ENTER_DELAY = 90;
const INITIAL_WAIT = 800;
const CHECK_DELAY = 600;
const RESET_DELAY = 1500;

const randomNames = [
  "Jane Alvarez",
  "Michael Stone",
  "Priya Rao",
  "David Lin",
  "Emily Kim",
  "Carlos Gomez",
  "John Wang",
  "Sara Hernandez",
  "Zachary Taylor",
  "Lina Park",
  "Ahmed Zaki",
  "Olivia Lee",
  "Noah Davis",
  "Liam Ellis",
  "Sophia Fox",
];

const insurers = ["Aetna", "Cigna", "BCBS", "UHC"];

type Status = "empty" | "checking" | "checked" | "failed";

interface Row {
  name: string;
  insurance: string;
  status: Status;
  id: string;
  willFail: boolean;
}

export default function AnimatedVerificationList() {
  const [rows, setRows] = useState<Row[]>([]);

  useEffect(() => {
    let timers: NodeJS.Timeout[] = [];

    const runCycle = () => {
      setRows([]);

      const numFailures = Math.random() < 0.5 ? 1 : 2;
      const failedIndices = new Set<number>();
      while (failedIndices.size < numFailures) {
        failedIndices.add(Math.floor(Math.random() * ROW_COUNT));
      }

      // Shuffle names and pick first ROW_COUNT
      const shuffledNames = [...randomNames]
        .sort(() => 0.5 - Math.random())
        .slice(0, ROW_COUNT);

      for (let i = 0; i < ROW_COUNT; i++) {
        const timer = setTimeout(() => {
          const name = shuffledNames[i];
          const insurance =
            insurers[Math.floor(Math.random() * insurers.length)];

          setRows((prev) => [
            ...prev,
            {
              name,
              insurance,
              status: "empty",
              id: `${Date.now()}-${i}`,
              willFail: failedIndices.has(i),
            },
          ]);
        }, i * ENTER_DELAY);
        timers.push(timer);
      }

      for (let i = 0; i < ROW_COUNT; i++) {
        const checkingTimer = setTimeout(() => {
          setRows((prev) =>
            prev.map((row, idx) =>
              idx === i ? { ...row, status: "checking" } : row
            )
          );
        }, ROW_COUNT * ENTER_DELAY + INITIAL_WAIT + i * CHECK_DELAY);
        timers.push(checkingTimer);

        const checkedTimer = setTimeout(() => {
          setRows((prev) =>
            prev.map((row, idx) =>
              idx === i
                ? { ...row, status: row.willFail ? "failed" : "checked" }
                : row
            )
          );
        }, ROW_COUNT * ENTER_DELAY + INITIAL_WAIT + i * CHECK_DELAY + 200);
        timers.push(checkedTimer);
      }

      const clearTimer = setTimeout(() => {
        setRows([]);
      }, ROW_COUNT * ENTER_DELAY + INITIAL_WAIT + ROW_COUNT * CHECK_DELAY + 200);

      const nextCycleTimer = setTimeout(() => {
        runCycle();
      }, ROW_COUNT * ENTER_DELAY + INITIAL_WAIT + ROW_COUNT * CHECK_DELAY + RESET_DELAY);

      timers.push(clearTimer, nextCycleTimer);
    };

    runCycle();
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="flex flex-col gap-3 h-full w-full p-4">
      <AnimatePresence initial={false}>
        {rows.map((row) => (
          <motion.div
            key={row.id}
            className={`flex items-center justify-between rounded-md px-4 py-3 border transition-colors duration-300 ${
              row.status === "checked"
                ? "bg-green-100 border-green-300"
                : row.status === "failed"
                ? "bg-red-100 border-red-300"
                : "bg-white border-zinc-300"
            }`}
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{
              opacity: 1,
              y: 0,
              scale: row.status === "checking" ? 1.02 : 1,
            }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{
              type: "spring",
              stiffness: 200,
              damping: 20,
              mass: 0.8,
            }}
          >
            <span className="text-lg font-medium text-zinc-700 flex items-center gap-2">
              {row.name}
              <span
                className={`text-sm font-light px-2 py-0.5 rounded-md transition-colors duration-300 ${
                  row.status === "checked"
                    ? "bg-green-200 text-green-800"
                    : row.status === "failed"
                    ? "bg-red-200 text-red-800"
                    : row.status === "checking"
                    ? "bg-blue-100 text-blue-700"
                    : "bg-zinc-100 text-zinc-600"
                }`}
              >
                {row.insurance}
              </span>
            </span>

            <motion.div
              animate={{
                scale: row.status === "checking" ? [1, 1.3, 1.1] : 1,
                rotate: row.status === "checking" ? [0, 5, -5, 0] : 0,
              }}
              transition={{
                duration: row.status === "checking" ? 0.4 : 0.2,
                ease: "easeInOut",
              }}
            >
              {row.status === "checked" ? (
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 15,
                  }}
                >
                  <CheckCircle className="text-green-500 w-6 h-6" />
                </motion.div>
              ) : row.status === "failed" ? (
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 15,
                  }}
                >
                  <XCircle className="text-red-500 w-6 h-6" />
                </motion.div>
              ) : (
                <Circle
                  className={`w-6 h-6 transition-colors duration-200 ${
                    row.status === "checking"
                      ? "text-blue-400"
                      : "text-zinc-400"
                  }`}
                />
              )}
            </motion.div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
